import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mic, Square, Loader2, Mail, MessageSquare, Copy, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/logger";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const NOTE_COLORS = ["yellow", "pink", "blue", "green", "peach", "lilac"];

export function ComposeDialog({ open, onClose, onCreate, onTranscribe, prefillBody = "", handle = "you", isPro = false, smsNumber = null }) {
  const [channel, setChannel] = useState("email");
  const [color, setColor] = useState("yellow");
  const [sender, setSender] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [showReal, setShowReal] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => { if (open && prefillBody) setBody(prefillBody); }, [open, prefillBody]);
  useEffect(() => { if (!open) { setSender(""); setSubject(""); setBody(""); setShowReal(false); setChannel("email"); setColor("yellow"); } }, [open]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/mpeg", "audio/ogg"];
      const mime = candidates.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = mr.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) { toast.message("Didn't record any audio — try again?"); return; }
        setTranscribing(true);
        try {
          const text = await onTranscribe(blob);
          if (text) setBody((b) => (b ? b + " " : "") + text);
          else toast.message("Didn't catch any words — try again?");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription failed");
        }
        setTranscribing(false);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      logError("Microphone error:", e);
      toast.error("Microphone permission is needed to record");
    }
  };

  const stopRec = () => { mediaRef.current?.stop(); setRecording(false); };

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    const isSms = channel === "sms";
    const name = sender || "A friend";
    const from = isSms
      ? (sender || "+15550000000")
      : (sender || "friend").toLowerCase().replace(/\s/g, "") + "@pinanote.app";
    await onCreate({ sender_name: name, sender: from, subject: isSms ? "" : subject, body, channel, color });
    setBusy(false);
    onClose();
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied!"); };
  const isSms = channel === "sms";
  const pill = (active) => `flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-display font-bold transition-colors ${active ? "bg-[#2b2620] text-[#fdf6e3]" : "bg-white/70 text-[#4a4238] hover:bg-white"}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="compose-dialog" className="max-w-md bg-[#fbf6ec] grain max-h-[88vh] overflow-auto board-scroll">
        <DialogHeader><DialogTitle className="font-display font-black text-xl text-[#2b2620]">Practice sending a message</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6b6155] -mt-2">Simulate an incoming email or text — it lands on your board just like the real thing.</p>

        <div className="flex gap-2 mt-1">
          <button data-testid="compose-channel-email" onClick={() => setChannel("email")} className={pill(!isSms)}><Mail className="w-4 h-4" /> Email</button>
          <button data-testid="compose-channel-sms" onClick={() => setChannel("sms")} className={pill(isSms)}><MessageSquare className="w-4 h-4" /> SMS text</button>
        </div>

        <div className="space-y-3 mt-1">
          <Input data-testid="compose-sender" placeholder={isSms ? "From (phone or name)" : "From (name)"} value={sender} onChange={(e) => setSender(e.target.value)} className="bg-white" />
          {!isSms && <Input data-testid="compose-subject" placeholder="Subject (optional)" value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-white" />}
          <Textarea data-testid="compose-body" placeholder={isSms ? "Type the text message…" : "Write the email…"} value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="bg-white font-note text-base" />

          <div className="flex items-center gap-2">
            {!recording ? (
              <Button type="button" data-testid="compose-record" onClick={startRec} disabled={transcribing} variant="outline" className="rounded-full gap-1.5 border-[#e0d3ba] bg-white hover:bg-[#f4ecdc] text-[#2b2620] font-display font-bold h-9">
                {transcribing ? <><Loader2 className="w-4 h-4 animate-spin" /> Transcribing…</> : <><Mic className="w-4 h-4" /> Record voice</>}
              </Button>
            ) : (
              <Button type="button" data-testid="compose-stop" onClick={stopRec} className="rounded-full gap-1.5 bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold h-9 animate-pulse">
                <Square className="w-4 h-4" fill="currentColor" /> Stop &amp; transcribe
              </Button>
            )}
            {recording && <span className="text-xs font-note text-[#8c7a55]">Listening…</span>}
          </div>

          <div>
            <span className="text-xs font-display font-bold text-[#6b6155]">Note color</span>
            <div className="flex gap-1.5 mt-1">
              {NOTE_COLORS.map((c) => (
                <button key={c} type="button" data-testid={`compose-color-${c}`} onClick={() => setColor(c)} title={c}
                  className={`note-${c} w-7 h-7 rounded-[3px] paper-shadow transition-transform ${color === c ? "ring-2 ring-[#2b2620] scale-110" : "hover:scale-105"}`} />
              ))}
            </div>
          </div>

          <Button data-testid="compose-submit" onClick={submit} disabled={busy || !body.trim()} className="w-full rounded-full bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold">
            {busy ? "Sending…" : isSms ? "Send practice text" : "Send practice email"}
          </Button>
        </div>

        <button data-testid="compose-show-real" onClick={() => setShowReal((v) => !v)} className="mt-1 flex items-center gap-1 text-xs font-display font-bold text-[#a0703c] hover:text-[#2b2620]">
          <ChevronDown className={`w-4 h-4 transition-transform ${showReal ? "rotate-180" : ""}`} /> Send one for real
        </button>
        {showReal && (
          <div data-testid="compose-real-panel" className="bg-white rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0"><Mail className="w-4 h-4 text-[#ff3b30] shrink-0" /><span className="font-note font-bold text-[#2b2620] truncate">{handle}@pinanote.app</span></div>
              <button data-testid="compose-copy-email" onClick={() => copy(`${handle}@pinanote.app`)} className="text-[#6b6155] hover:text-[#2b2620] shrink-0"><Copy className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-[#6b6155]">Open any email app and send a message to that address — it arrives as a sticky note automatically.</p>
            <div className="flex items-center justify-between gap-2 border-t border-[#eee3ce] pt-2">
              <div className="flex items-center gap-2 min-w-0"><MessageSquare className="w-4 h-4 text-[#a0703c] shrink-0" /><span className="font-note font-bold text-[#2b2620] truncate">{smsNumber ? `Text ${smsNumber}` : (isPro ? "SMS number — coming soon" : "SMS number (Pro)")}</span></div>
              {smsNumber && <button data-testid="compose-copy-sms" onClick={() => copy(smsNumber)} className="text-[#6b6155] hover:text-[#2b2620] shrink-0"><Copy className="w-4 h-4" /></button>}
            </div>
            <p className="text-xs text-[#6b6155]">{smsNumber ? "Link your mobile in Profile, then text this number — it lands on your board." : (isPro ? "Your SMS number is being set up — coming soon." : "Upgrade to Pro to text notes straight onto your board.")}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
